'use client';

import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWeekend,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { enUS, zhCN, zhTW } from 'date-fns/locale';
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@flipster/react/design-system/button';
import { Dropdown } from '@flipster/react/design-system/dropdown';
import { useDropdown } from '@flipster/react/design-system/dropdown/dropdown';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Modal } from '@flipster/react/design-system/modal';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import ChevronLeftIcon from '@flipster/react/icon/generated/ChevronLeftIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { css } from '@flipster/styled-system/css';
import { convertDate } from '@flipster/utils/datetime';

import { ResponsiveOverlay } from '../responsive-overlay';
import { datePickerRecipe } from './date-picker.recipe';

interface DatePickerBaseProps {
  min?: Date;
  max?: Date;
  disabled?: boolean;
  confirmText?: ReactNode;
  isUtc?: boolean;
  portal?: boolean;
}

interface DayPickerProps extends DatePickerBaseProps {
  selected?: Date | string;
  onChange?: (date: Date) => void;
}

export interface DateRange {
  startDate: Date | string;
  endDate: Date | string;
}

interface RangePickerProps extends DatePickerBaseProps {
  selected?: DateRange;
  onChange?: (range: { startDate: Date; endDate: Date } | undefined) => void;
  isMonthly?: boolean;
  maxRangeDays?: number;
  /** 시작일을 고정한다. 종료일만 바꿀 수 있다. */
  disableStart?: boolean;
}

type DisplayedTimeUnit = 'week' | 'month';

interface DatePickerContextType {
  slots: ReturnType<typeof datePickerRecipe.raw>;
  displayedTimeUnit: DisplayedTimeUnit;
  year: number;
  month: number;
  current?: Date;
  range?: [Date, Date];
  previewRange?: [Date, Date];
  onChange?: (date: Date) => void;
  onHoverDate?: (date: Date | undefined) => void;
  min?: Date;
  max?: Date;
  setYear: (year: number) => void;
  setMonth: (month: number) => void;
  setDisplayedTimeUnit: (unit: DisplayedTimeUnit) => void;
  isUtc?: boolean;
  disabled?: boolean;
  isMonthly?: boolean;
}

interface DatePickerLocaleProps {
  locale: string;
  startText: ReactNode;
  endText: ReactNode;
  startDateText: ReactNode;
  endDateText: ReactNode;
  confirmText: ReactNode;
  closeText: ReactNode;
}

export const DatePickerLocaleContext = createContext<DatePickerLocaleProps | null>(null);

const useDatePickerLocale = () => {
  const ctx = use(DatePickerLocaleContext);
  if (!ctx) throw new Error('DatePickerLocaleContext not found in context');
  return ctx;
};

export const DatePickerLocaleProvider = ({
  locale,
  startText,
  endText,
  startDateText,
  endDateText,
  confirmText,
  closeText,
  children,
}: PropsWithChildren<DatePickerLocaleProps>) => {
  const contextProps = useMemo(
    () => ({ locale, startText, endText, startDateText, endDateText, confirmText, closeText }),
    [locale, startText, endText, startDateText, endDateText, confirmText, closeText],
  );
  return <DatePickerLocaleContext value={contextProps}>{children}</DatePickerLocaleContext>;
};

const DatePickerContext = createContext<DatePickerContextType | null>(null);

const useDatePicker = () => {
  const ctx = use(DatePickerContext);
  if (!ctx) throw new Error('DatePickerContext not found in context');
  return ctx;
};

const Root = ResponsiveOverlay.Root;

const Trigger = ResponsiveOverlay.Trigger;

const CalendarGrid = ({ children }: { children?: ReactNode }) => {
  const { slots, displayedTimeUnit } = useDatePicker();
  return (
    <div className={css(slots.root)}>
      <Header />
      <div className={css(slots.gridWrapper)}>
        <WeekGrid />
        {displayedTimeUnit === 'month' && <MonthGrid />}
      </div>
      {children}
    </div>
  );
};

interface PickerLayoutProps {
  content: ReactNode;
  confirmLabel?: ReactNode;
  confirmDisabled?: boolean;
  slots: ReturnType<typeof datePickerRecipe.raw>;
  onClose: () => void;
  onConfirm: () => void;
  portal?: boolean;
}

const PickerLayout = ({
  content,
  confirmLabel,
  confirmDisabled,
  slots,
  onClose,
  onConfirm,
  portal,
}: PickerLayoutProps) => {
  const { confirmText: defaultConfirmText, closeText } = useDatePickerLocale();
  const label = confirmLabel ?? defaultConfirmText;

  return (
    <ResponsiveOverlay.Content
      dropdown={
        <Dropdown.Content portal={portal}>
          {content}
          <div className={css(slots.dropdownFooter)}>
            <Button
              hierarchy='secondary'
              color='neutral'
              size='large'
              label={closeText}
              css={slots.dropdownButton}
              onClick={onClose}
            />
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              label={label}
              css={slots.dropdownButton}
              disabled={confirmDisabled}
              onClick={onConfirm}
            />
          </div>
        </Dropdown.Content>
      }
      modal={
        <>
          <Modal.Content>{content}</Modal.Content>
          <Modal.Footer>
            <Modal.Button hierarchy='secondary' color='neutral' size='large' label={closeText} onClick={onClose} />
            <Modal.Button
              hierarchy='primary'
              color='accent'
              size='large'
              label={label}
              disabled={confirmDisabled}
              onClick={onConfirm}
            />
          </Modal.Footer>
        </>
      }
    />
  );
};

const DayPicker = ({ selected, min, max, disabled, confirmText, isUtc, portal, onChange }: DayPickerProps) => {
  const previousDateRef = useRef<string | undefined>(undefined);
  const [displayedTimeUnit, setDisplayedTimeUnit] = useState<DisplayedTimeUnit>('week');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [value, setValue] = useState<Date | undefined>(undefined);

  const { open, setOpen } = useDropdown();

  const slots = useMemo(() => datePickerRecipe.raw({ displayedTimeUnit }), [displayedTimeUnit]);
  const hasUnsavedChanges = !!value && previousDateRef.current !== formatDate({ date: value });

  useEffect(() => {
    if (open) {
      const rawValue = convertDate(selected) || new Date();
      const currentValue = toDisplayDate(rawValue, isUtc);
      setValue(currentValue);
      setYear(currentValue.getFullYear());
      setMonth(currentValue.getMonth());
      previousDateRef.current = selected ? formatDate({ date: currentValue }) : undefined;
    }
  }, [open, selected, isUtc]);

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const handleConfirm = useCallback(() => {
    if (!value || !onChange) return;
    onChange(
      isUtc
        ? new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()))
        : new Date(value.getFullYear(), value.getMonth(), value.getDate()),
    );
    setOpen(false);
  }, [value, onChange, isUtc, setOpen]);

  const displayMin = useMemo(() => (min ? toDisplayDate(min, isUtc) : undefined), [min, isUtc]);
  const displayMax = useMemo(() => (max ? toDisplayDate(max, isUtc) : undefined), [max, isUtc]);

  const contextValue: DatePickerContextType = {
    slots,
    displayedTimeUnit,
    year,
    month,
    current: value,
    min: displayMin,
    max: displayMax,
    onChange: setValue,
    setYear,
    setMonth,
    setDisplayedTimeUnit,
    isUtc,
    disabled,
  };

  const content = (
    <DatePickerContext value={contextValue}>
      <CalendarGrid />
    </DatePickerContext>
  );

  return (
    <PickerLayout
      content={content}
      confirmLabel={confirmText}
      confirmDisabled={!hasUnsavedChanges || disabled}
      slots={slots}
      onClose={handleClose}
      onConfirm={handleConfirm}
      portal={portal}
    />
  );
};

const Tabs = createTabsComponent<'start' | 'end'>();

interface PreviousDateRange {
  start: string;
  end: string;
}

const RangePicker = ({
  selected,
  min,
  max,
  disabled,
  confirmText,
  isUtc,
  portal,
  onChange,
  isMonthly,
  maxRangeDays,
  disableStart,
}: RangePickerProps) => {
  const previousDateRef = useRef<PreviousDateRange | undefined>(undefined);
  const [displayedTimeUnit, setDisplayedTimeUnit] = useState<DisplayedTimeUnit>('week');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [editingDateField, setEditingDateField] = useState<'start' | 'end'>('start');
  const [value, setValue] = useState<[Date | undefined, Date | undefined]>([undefined, undefined]);
  const [hoveredDate, setHoveredDate] = useState<Date | undefined>(undefined);

  const { open, setOpen } = useDropdown();
  const { startText, endText, startDateText, endDateText } = useDatePickerLocale();

  const slots = useMemo(() => datePickerRecipe.raw({ displayedTimeUnit }), [displayedTimeUnit]);

  const [startValue, endValue] = value;

  const hasPreviousSelection = !!previousDateRef.current;
  const isBothEmpty = !startValue && !endValue;
  const isCleared = isBothEmpty && hasPreviousSelection;
  const isRangeChanged =
    startValue != null &&
    endValue != null &&
    (!hasPreviousSelection ||
      formatDate({ date: startValue }) !== previousDateRef.current?.start ||
      formatDate({ date: endValue }) !== previousDateRef.current?.end);

  const hasUnsavedChanges = isRangeChanged || isCleared;

  useEffect(() => {
    if (open) {
      if (isMonthly) setDisplayedTimeUnit('month');
      const rawStart = convertDate(selected?.startDate) ?? new Date();
      const rawEnd = convertDate(selected?.endDate) ?? rawStart;
      const start = toDisplayDate(rawStart, isUtc);
      const end = toDisplayDate(rawEnd, isUtc);

      const initialField = disableStart ? 'end' : 'start';
      setEditingDateField(initialField);
      const focusDate = initialField === 'end' ? end : start;
      setYear(focusDate.getFullYear());
      setMonth(focusDate.getMonth());
      setHoveredDate(undefined);

      if (selected) {
        setValue([start, end]);
        previousDateRef.current = {
          start: formatDate({ date: start }),
          end: formatDate({ date: end }),
        };
      } else {
        setValue([undefined, undefined]);
        previousDateRef.current = undefined;
      }
    }
  }, [open, selected, isUtc, isMonthly, disableStart]);

  const handleDateChange = (date: Date) => {
    setHoveredDate(undefined);

    if (editingDateField === 'start' && disableStart) return;

    if (editingDateField === 'start') {
      // If new start is after current end, clear end
      const keepEnd = value[1] && !isAfter(date, value[1]);
      setValue([date, keepEnd ? value[1] : undefined]);
      setEditingDateField('end');

      // Navigate to end date's month if end is still valid
      if (!isMonthly && keepEnd && value[1]) {
        setYear(value[1].getFullYear());
        setMonth(value[1].getMonth());
      }
    } else {
      setValue([value[0], date]);
    }
  };

  const handleTabChange = useCallback(
    (field: 'start' | 'end') => {
      if (field === 'start' && disableStart) return;
      setEditingDateField(field);
      setHoveredDate(undefined);
      const targetDate = field === 'start' ? value[0] : value[1];
      if (targetDate) {
        setYear(targetDate.getFullYear());
        setMonth(targetDate.getMonth());
      }
    },
    [value, disableStart],
  );

  const previewRange = useMemo<[Date, Date] | undefined>(() => {
    if (!hoveredDate) return undefined;

    if (editingDateField === 'end' && value[0]) {
      if (isBefore(hoveredDate, value[0])) return undefined;
      return [value[0], hoveredDate];
    }

    if (editingDateField === 'start' && value[1]) {
      if (isAfter(hoveredDate, value[1])) return undefined;
      return [hoveredDate, value[1]];
    }

    return undefined;
  }, [hoveredDate, editingDateField, value]);

  const displayMin = useMemo(() => {
    const baseMin = min ? toDisplayDate(min, isUtc) : undefined;

    if (editingDateField === 'end' && value[0]) {
      if (!baseMin) return value[0];
      return isAfter(value[0], baseMin) ? value[0] : baseMin;
    }

    if (editingDateField === 'start' && value[1] && maxRangeDays != null) {
      const minByRange = subDays(value[1], maxRangeDays - 1);
      if (!baseMin) return minByRange;
      return isAfter(minByRange, baseMin) ? minByRange : baseMin;
    }

    return baseMin;
  }, [min, isUtc, editingDateField, value, maxRangeDays]);

  const displayMax = useMemo(() => {
    const baseMax = max ? toDisplayDate(max, isUtc) : undefined;

    if (editingDateField === 'start' && value[1]) {
      if (!baseMax) return value[1];
      return isBefore(value[1], baseMax) ? value[1] : baseMax;
    }

    if (editingDateField === 'end' && value[0] && maxRangeDays != null) {
      const maxByRange = addDays(value[0], maxRangeDays - 1);
      if (!baseMax) return maxByRange;
      return isBefore(maxByRange, baseMax) ? maxByRange : baseMax;
    }

    return baseMax;
  }, [max, isUtc, editingDateField, value, maxRangeDays]);

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const handleConfirm = useCallback(() => {
    if (!value[0] || !value[1]) {
      if (!value[0] && !value[1]) {
        onChange?.(undefined);
        setOpen(false);
      }
      return;
    }

    let [start, end] = value;
    if (isAfter(start, end)) [start, end] = [end, start];

    const confirmedStart = toRangeStart(start, isUtc, isMonthly);
    const confirmedEnd = toRangeEnd(end, isUtc, isMonthly);

    onChange?.({ startDate: confirmedStart, endDate: confirmedEnd });
    setOpen(false);
  }, [value, onChange, isUtc, isMonthly, setOpen]);

  const handleClear = useCallback(() => {
    setValue((prev) => [disableStart ? prev[0] : undefined, undefined]);
    setEditingDateField(disableStart ? 'end' : 'start');
    setHoveredDate(undefined);
  }, [disableStart]);

  const contextValue: DatePickerContextType = {
    slots,
    displayedTimeUnit,
    year,
    month,
    current: editingDateField === 'start' ? value[0] : value[1],
    range: value[0] && value[1] ? [value[0], value[1]] : undefined,
    previewRange,
    min: displayMin,
    max: displayMax,
    onChange: handleDateChange,
    onHoverDate: setHoveredDate,
    setYear,
    setMonth,
    setDisplayedTimeUnit,
    isUtc,
    disabled,
    isMonthly,
  };

  const content = (
    <>
      <Tabs type='fixed' size='medium' border value={editingDateField} onChange={handleTabChange}>
        <Tabs.Tab label={startText} value='start' />
        <Tabs.Tab label={endText} value='end' />
      </Tabs>
      <DatePickerContext value={contextValue}>
        <CalendarGrid>
          <div className={css(slots.fakeInput)}>
            <button
              type='button'
              className={css(slots.fakeInputValue)}
              data-placeholder={!value[0]}
              disabled={disableStart}
              onClick={() => handleTabChange('start')}
            >
              {value[0]
                ? formatDate({ date: value[0], formatStr: isMonthly ? 'yyyy-MM' : 'yyyy-MM-dd' })
                : startDateText}
            </button>
            <span>~</span>
            <button
              type='button'
              className={css(slots.fakeInputValue)}
              data-placeholder={!value[1]}
              onClick={() => handleTabChange('end')}
            >
              {value[1] ? formatDate({ date: value[1], formatStr: isMonthly ? 'yyyy-MM' : 'yyyy-MM-dd' }) : endDateText}
            </button>
            <IconButton
              hierarchy='quaternary'
              size='medium'
              icon={<XCircleIcon />}
              onClick={handleClear}
              disabled={disabled || (disableStart ? !value[1] : !value[0] && !value[1])}
            />
          </div>
        </CalendarGrid>
      </DatePickerContext>
    </>
  );

  return (
    <PickerLayout
      content={content}
      confirmLabel={confirmText}
      confirmDisabled={!hasUnsavedChanges || disabled}
      slots={slots}
      onClose={handleClose}
      onConfirm={handleConfirm}
      portal={portal}
    />
  );
};

DayPicker.displayName = 'DatePicker.DayPicker';
RangePicker.displayName = 'DatePicker.RangePicker';
export { Root, Trigger, DayPicker, RangePicker };

const Header = () => {
  const { locale } = useDatePickerLocale();
  const { slots, displayedTimeUnit, year, month, setYear, setMonth, setDisplayedTimeUnit, min, max, isMonthly } =
    useDatePicker();

  const handlePreviousClick = () => {
    if (displayedTimeUnit === 'month') {
      setYear(year - 1);
      return;
    }
    if (month !== 0) setMonth(month - 1);
    else {
      setYear(year - 1);
      setMonth(11);
    }
  };

  const handleNextClick = () => {
    if (displayedTimeUnit === 'month') {
      setYear(year + 1);
      return;
    }
    if (month !== 11) setMonth(month + 1);
    else {
      setYear(year + 1);
      setMonth(0);
    }
  };

  const handleCaptionClick = () => {
    if (isMonthly) return;
    setDisplayedTimeUnit(displayedTimeUnit === 'week' ? 'month' : 'week');
  };

  const isPrevDisabled = (() => {
    if (!min) return false;
    if (displayedTimeUnit === 'month') return min.getFullYear() >= year;
    if (min.getFullYear() > year) return true;
    if (min.getFullYear() === year && min.getMonth() >= month) return true;
    return false;
  })();

  const isNextDisabled = (() => {
    if (!max) return false;
    if (displayedTimeUnit === 'month') return max.getFullYear() <= year;
    if (max.getFullYear() < year) return true;
    if (max.getFullYear() === year && max.getMonth() <= month) return true;
    return false;
  })();

  return (
    <div className={css(slots.header)}>
      <IconButton
        hierarchy='quaternary'
        size='medium'
        color='content.default.level-5'
        icon={<ChevronLeftIcon />}
        onClick={handlePreviousClick}
        disabled={isPrevDisabled}
      />
      {isMonthly && <div className={css(slots.caption)}>{year}</div>}
      {!isMonthly && (
        <button
          type='button'
          className={css(slots.caption)}
          onClick={handleCaptionClick}
          aria-expanded={displayedTimeUnit === 'month'}
        >
          {formatYearMonth(year, month, locale)}
          <CaretDownIcon size={18} />
        </button>
      )}
      <IconButton
        hierarchy='quaternary'
        size='medium'
        color='content.default.level-5'
        icon={<ChevronRightIcon />}
        onClick={handleNextClick}
        disabled={isNextDisabled}
      />
    </div>
  );
};

const MonthGrid = () => {
  const { locale } = useDatePickerLocale();
  const {
    slots,
    year,
    min,
    max,
    current,
    range,
    previewRange,
    setMonth,
    setDisplayedTimeUnit,
    onChange,
    onHoverDate,
    isMonthly,
  } = useDatePicker();

  const effectiveRange = previewRange ?? range;

  return (
    <div className={css(slots.monthGrid)} onMouseLeave={() => onHoverDate?.(undefined)}>
      {Array.from({ length: 12 }, (_, i) => i).map((monthIndex) => {
        const date = new Date(year, monthIndex, 1);
        const monthLabel = formatDate({ date, formatStr: 'LLL', locale });
        const monthDataAttr = formatDate({ date, formatStr: 'yyyy-MM' });
        const rangeStartYear = effectiveRange?.[0].getFullYear();
        const rangeStartMonth = effectiveRange?.[0].getMonth();
        const rangeEndYear = effectiveRange?.[1].getFullYear();
        const rangeEndMonth = effectiveRange?.[1].getMonth();
        const isInRange =
          isMonthly &&
          !!effectiveRange &&
          (year > rangeStartYear! || (year === rangeStartYear && monthIndex >= rangeStartMonth!)) &&
          (year < rangeEndYear! || (year === rangeEndYear && monthIndex <= rangeEndMonth!));
        const isPreview = isInRange && effectiveRange === previewRange;
        const isSelected =
          !previewRange && !!current && current.getFullYear() === year && current.getMonth() === monthIndex;
        const isDisabled =
          (min && (year < min.getFullYear() || (year === min.getFullYear() && monthIndex < min.getMonth()))) ||
          (max && (year > max.getFullYear() || (year === max.getFullYear() && monthIndex > max.getMonth())));

        return (
          <Button
            size='medium'
            hierarchy='primary'
            color={isSelected || isInRange ? 'accent' : 'neutral'}
            label={monthLabel}
            data-month={monthDataAttr}
            data-selected={isSelected}
            data-range={isInRange}
            data-range-preview={isPreview}
            onClick={() => {
              if (isMonthly) {
                onChange?.(new Date(year, monthIndex, 1));
              } else {
                setMonth(monthIndex);
                setDisplayedTimeUnit('week');
              }
            }}
            onMouseEnter={() => onHoverDate?.(isDisabled ? undefined : date)}
            disabled={isDisabled}
            key={monthDataAttr}
          />
        );
      })}
    </div>
  );
};

const WeekGrid = () => {
  const { locale } = useDatePickerLocale();
  const { slots, year, month, current, range, previewRange, min, max, onChange, onHoverDate, disabled } =
    useDatePicker();

  const weekStartsOn = locale.startsWith('zh-') ? 1 : 0;
  const calendarDays = useMemo(() => getCalendarDays(year, month, weekStartsOn), [year, month, weekStartsOn]);
  const weekDays = useMemo(() => getWeekDayFullNames(weekStartsOn), [weekStartsOn]);
  const weekDayLabels = getWeekDayLabels(locale);
  const todayString = useMemo(() => formatDate({ date: new Date() }), []);

  return (
    <div className={css(slots.weekGrid)} onMouseLeave={() => onHoverDate?.(undefined)}>
      {weekDayLabels.map((label, index) => (
        <div className={css(slots.dayLabel)} aria-label={weekDays[index]} key={label + index}>
          {label}
        </div>
      ))}
      {calendarDays.map((date) => {
        const dateString = formatDate({ date });

        const effectiveRange = previewRange ?? range;
        const isInRange = !!effectiveRange && !isBefore(date, effectiveRange[0]) && !isAfter(date, effectiveRange[1]);
        const isPreview = isInRange && effectiveRange === previewRange;
        const isSelected = !previewRange && !!current && isSameDay(date, current);
        const isDisabled = disabled || (!!min && isBefore(date, min)) || (!!max && isAfter(date, max));
        const isToday = dateString === todayString;

        return (
          <button
            type='button'
            key={dateString}
            title={dateString}
            data-day={dateString}
            data-today={isToday || undefined}
            data-selected={isSelected || undefined}
            data-range={isInRange || undefined}
            data-range-preview={isPreview || undefined}
            data-weekend={isWeekend(date) || undefined}
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) onChange?.(date);
            }}
            onMouseEnter={() => onHoverDate?.(isDisabled ? undefined : date)}
            className={css(slots.day)}
          >
            {date.getDate()}
          </button>
        );
      })}
    </div>
  );
};

function getWeekDayLabels(locale: string): string[] {
  if (locale.startsWith('zh-')) return ['一', '二', '三', '四', '五', '六', '日'];
  return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
}

function getWeekDayFullNames(weekStartsOn: 0 | 1): string[] {
  const weekStart = startOfWeek(new Date(), { weekStartsOn });
  const weekEnd = endOfWeek(new Date(), { weekStartsOn });
  return eachDayOfInterval({ start: weekStart, end: weekEnd }).map((date) => format(date, 'EEEE'));
}

const minCalenderWeeks = 5;

function getCalendarDays(year: number, month: number, weekStartsOn: 0 | 1 = 0): Date[] {
  const monthStart = new Date(year, month, 1);
  const calendarStart = startOfWeek(startOfMonth(monthStart), { weekStartsOn });
  const calendarEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const totalWeeks = days.length / 7;
  if (totalWeeks < minCalenderWeeks) {
    const lastDay = days[days.length - 1];
    const nextDay = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1);
    const extendedEnd = endOfWeek(nextDay, { weekStartsOn });
    return eachDayOfInterval({ start: calendarStart, end: extendedEnd });
  }

  return days;
}

function formatYearMonth(year: number, month: number, locale?: string): string {
  return formatDate({ date: new Date(year, month, 1), formatStr: 'LLL yyyy', locale });
}

function formatDate({
  date,
  formatStr = 'yyyy-MM-dd',
  locale,
}: {
  date: Date;
  formatStr?: string;
  locale?: string;
}): string {
  if (formatStr.includes('LLL') && locale) {
    const dateFnsLocale = getDateFnsLocale(locale);
    return format(date, formatStr, { locale: dateFnsLocale });
  }
  return format(date, formatStr);
}

function getDateFnsLocale(locale: string) {
  if (locale === 'zh-CN') return zhCN;
  if (locale === 'zh-TW') return zhTW;
  return enUS;
}

function toDisplayDate(date: Date, isUtc?: boolean): Date {
  if (isUtc) return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toRangeStart(date: Date, isUtc?: boolean, isMonthly?: boolean): Date {
  if (isMonthly) {
    return isUtc
      ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0))
      : startOfMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }
  return isUtc
    ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
    : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function toRangeEnd(date: Date, isUtc?: boolean, isMonthly?: boolean): Date {
  if (isMonthly) {
    return isUtc
      ? new Date(Date.UTC(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 59))
      : endOfMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }
  return isUtc
    ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 59))
    : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 59);
}
